import { queryOptions, useQuery } from '@tanstack/react-query';
import { queryClient } from 'utils/query';

export type LowFrequencyCollection =
    | { enabled: false }
    | {
          enabled: true;
          samplingFrequencyHz: number;
          numberOfSamples: number;
      };

export type UpdateInterval =
    | { mode: 'smartsample' }
    | {
          mode: 'triggered';
          thresholdG: number;
          collectionOffsetMs: number;
          minTimeBetweenTriggersMinutes: number;
      }
    | { mode: 'timed'; intervalMinutes: number };

export type EnvelopedData =
    | { enabled: false }
    | {
          enabled: true;
          samplingFrequencyHz: number;
          numberOfSamples: number;
      };

export type V4SensorSettings = {
    vibrationX: boolean;
    vibrationY: boolean;
    vibrationZ: boolean;
    samplingFrequencyHz: number;
    numberOfSamples: number;
    lowFrequencyCollection: LowFrequencyCollection;
    updateInterval: UpdateInterval;
    envelopedData: EnvelopedData;
    ultrasound: boolean;
    turningSpeed: boolean;
};

export const defaultV4SensorSettings: V4SensorSettings = {
    vibrationX: true,
    vibrationY: true,
    vibrationZ: true,
    samplingFrequencyHz: 16000,
    numberOfSamples: 8000,
    lowFrequencyCollection: { enabled: false },
    updateInterval: { mode: 'smartsample' },
    envelopedData: {
        enabled: true,
        samplingFrequencyHz: 2000,
        numberOfSamples: 1000,
    },
    ultrasound: true,
    turningSpeed: true,
};

type V4SensorSettingsQueryConfig = {
    locationId: string;
};

export const v4SensorSettingsQueries = {
    detail: ({ locationId }: V4SensorSettingsQueryConfig) =>
        queryOptions({
            queryKey: ['v4-sensor-settings', locationId] as const,
            // TODO: replace with real SDK call when the V4 sensor settings API is available.
            queryFn: (): Promise<V4SensorSettings> =>
                Promise.resolve(defaultV4SensorSettings),
        }),
};

export function useV4SensorSettings(config: V4SensorSettingsQueryConfig) {
    return useQuery(v4SensorSettingsQueries.detail(config));
}

export function invalidateV4SensorSettings(locationId: string) {
    queryClient.invalidateQueries({
        queryKey: v4SensorSettingsQueries.detail({ locationId }).queryKey,
    });
}
